import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { venueImages } from '../../../constants/realVenueData';
import { shouldShowVenuePricing } from '../../../utils/venuePricing';
import { buildVenuePath } from '../../../utils/venueSlug';
import { warmVenueDiscoveryCache } from '../../../utils/venueDiscoveryCache';
import './VenueCard.css';

const PLACEHOLDER_IMAGE = venueImages[0];

const VenueCard = ({ venue, searchParams }) => {
  const [imgError, setImgError] = useState(false);

  const params = new URLSearchParams();
  if (searchParams.location) params.set('location', searchParams.location);
  if (searchParams.date) params.set('date', searchParams.date);
  if (searchParams.eventType) params.set('eventType', searchParams.eventType);
  if (searchParams.venueBriefRequestId) {
    params.set('venueBriefRequestId', searchParams.venueBriefRequestId);
  }
  const venueHref = buildVenuePath(venue, { searchParams: params });

  return (
    <div className="venue-card">
      <Link
        className="venue-card-link"
        to={venueHref}
        state={{ venueSummary: venue }}
        onMouseEnter={() => warmVenueDiscoveryCache(venue.id)}
        style={{ cursor: 'pointer', textDecoration: 'none' }}
      >
        <div className="venue-image">
          <img
            src={imgError ? PLACEHOLDER_IMAGE : (venue.image || PLACEHOLDER_IMAGE)}
            alt={venue.name}
            onError={() => setImgError(true)}
          />
          {venue.vibe && (
            <div className="venue-vibe">
              {venue.vibe}
            </div>
          )}
        </div>
        <div className="venue-content">
          <div className="venue-title-section-search-results">
            <div className="venue-title-row">
              <h3 className="venue-title-search-results">{venue.name}</h3>
              {shouldShowVenuePricing(venue) && venue.price != null && (
                <p className="venue-price">{venue.price}</p>
              )}
            </div>
            <div className="venue-rating-capacity">
              <span className="venue-rating">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M19.628 10.5792L17.3534 12.7713C17.0411 13.0718 16.829 13.6728 16.8938 14.103L17.2474 16.6721C17.4772 18.3162 16.4578 19.0292 14.9846 18.2631L12.4862 16.955C12.0678 16.7369 11.3843 16.7487 10.9777 16.9903L8.79155 18.269C7.06503 19.2766 6.03384 18.5165 6.49345 16.572L7.13574 13.8555C7.2536 13.3605 7.03557 12.6829 6.66434 12.347L4.59605 10.4791C3.11702 9.14144 3.5295 7.92758 5.52118 7.77437L8.0432 7.58581C8.5146 7.55046 9.08618 7.17922 9.30421 6.76085L10.6124 4.25062C11.3843 2.78338 12.6276 2.78927 13.376 4.2683L14.5427 6.57818C14.743 6.96709 15.2498 7.34422 15.68 7.40903L18.803 7.91579C20.4883 8.19864 20.8595 9.39483 19.628 10.5792Z"
                    fill="#FFC10D"/>
                </svg>
                {venue.rating}
              </span>
              <span className="venue-separator">•</span>
              <span className="venue-capacity">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M7.63314 9.05866C7.5498 9.05033 7.4498 9.05033 7.35814 9.05866C5.3748 8.99199 3.7998 7.36699 3.7998 5.36699C3.7998 3.32533 5.4498 1.66699 7.4998 1.66699C9.54147 1.66699 11.1998 3.32533 11.1998 5.36699C11.1915 7.36699 9.61647 8.99199 7.63314 9.05866Z"
                    stroke="#0E1012" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                  <path
                    d="M13.6752 3.33301C15.2919 3.33301 16.5919 4.64134 16.5919 6.24967C16.5919 7.82467 15.3419 9.10801 13.7836 9.16634C13.7169 9.15801 13.6419 9.15801 13.5669 9.16634"
                    stroke="#0E1012" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                  <path
                    d="M3.4666 12.133C1.44993 13.483 1.44993 15.683 3.4666 17.0247C5.75827 18.558 9.5166 18.558 11.8083 17.0247C13.8249 15.6747 13.8249 13.4747 11.8083 12.133C9.52494 10.608 5.7666 10.608 3.4666 12.133Z"
                    stroke="#0E1012" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                  <path
                    d="M15.2832 16.667C15.8832 16.542 16.4499 16.3003 16.9165 15.942C18.2165 14.967 18.2165 13.3587 16.9165 12.3837C16.4582 12.0337 15.8999 11.8003 15.3082 11.667"
                    stroke="#0E1012" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {venue.capacity}
              </span>
            </div>
          </div>
          <div className="venue-distance">
            <svg xmlns="http://www.w3.org/2000/svg" width="24.613" height="20" viewBox="0 0 25 20" fill="none">
              <g clipPath="url(#clip0_467_718)">
                <path
                  d="M18.7779 9.99929C18.7779 13.5624 15.8876 16.4518 12.3244 16.4518C8.76043 16.4518 5.87249 13.5624 5.87249 9.99929C5.87249 6.43675 8.76048 3.54821 12.3244 3.54821C15.8876 3.54816 18.7779 6.43675 18.7779 9.99929ZM12.3244 0C6.80089 0 2.32373 4.47716 2.32373 9.99929C2.32373 15.5225 6.80089 20 12.3244 20C17.8468 20 22.3237 15.5225 22.3237 9.99929C22.3237 4.47716 17.8468 0 12.3244 0Z"
                  fill="#E1251B"/>
                <path d="M24.613 7.98047H0V12.0255H24.613V7.98047Z" fill="#000F9F"/>
              </g>
              <defs>
                <clipPath id="clip0_467_718">
                  <rect width="24.613" height="20" fill="white"/>
                </clipPath>
              </defs>
            </svg>
            {venue.distance}
          </div>
        </div>
      </Link>

    </div>
  );
};

export default VenueCard;

